import type React from 'react'
import { TooltipProvider } from '@components/ui/tooltip'
import { useActiveObject } from '@/components/app/editor/active-object-context'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { OFFSET_SIDES } from '../constants/wrap-offsets'
import { applyLiveOffsetsToAll, applyLiveSideOffsetToAll } from '../handlers/live-offset-preview'
import { validateOffsetInput } from '../helpers/offset-input'
import { useCommitToTargets } from '../hooks/use-commit-to-targets'
import { useCommonOffsetInput } from '../hooks/use-common-offset-input'
import { useOffsetCommit } from '../hooks/use-offset-commit'
import { useSideOffsetInputs } from '../hooks/use-side-offset-inputs'
import { useWrapOffsetSync } from '../hooks/use-wrap-offset-sync'
import { useWrapStyleSwitch } from '../hooks/use-wrap-style-switch'
import { useWrapStyles } from '../hooks/use-wrap-styles'
import { CommonOffsetSlider } from './common-offset-slider'
import { OffsetPresetToggle } from './offset-preset-toggle'
import { SideOffsetInput } from './side-offset-input'
import { WrapStyleButtons } from './wrap-style-buttons'

export interface LayoutPanelTextWrapProps {
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  onBatchShapesCommitted?: (
    items: BatchShapeTransformItem[],
    source: 'rotate' | 'move' | 'align' | 'resize' | 'update'
  ) => void
  /** Mirrors `LayoutPanel`'s own `interactionLocked` (selection inapplicable/locked) — folded into
   *  `controlsDisabled` (`useWrapStyles`) so every wrap-style/offset control disables along with
   *  the rest of the Layout section, not just the target-eligibility check this file already had. */
  interactionLocked?: boolean
}

/**
 * Text-wrap style for the selected shape(s)/group(s)/text frame(s) — nearby text reflows around
 * them when set to "Bounding box", "Wrap Shape", or "Jump" (see `compute-wrap-obstacles.ts`/
 * `shape-contour-wrap.ts`; AC 2.1, AC 3.1). Eligible for rectangle, irregular polygon (covers
 * circles/stars/custom shapes), polyline, regular-polygon kinds, and a group row
 * (`isWrapSourceEligibleShape`) — a placed photo is one of these same shapes carrying a
 * `photoFill`, not a separate kind, so it's covered too (AC 3.3); a text frame is one of these
 * same shapes carrying `paragraphs`, not a separate kind either. A group's own wrap is stored on
 * its `ShapeData` row exactly like a plain shape's, independently of its children's individual
 * wrap settings. "Bounding box"/"Jump" resolve to the group's LIVE outer union (never its
 * possibly-stale stored geometry) as one single band — see `computeLiveGroupWrapRect` in
 * `rawBandsForTextBox`. "Wrap Shape" instead decomposes into each of the group's own leaf
 * members' own contour/rect, independently, so text can flow into empty space between/around
 * individual members rather than being blocked by the group's outer box — see
 * `collectLeafDescendants`/the group branch in `rawBandsForTextBox`. Both `wrapStyle` and the
 * four directional offsets persist across a reload for every eligible kind (`buildTextWrap` in
 * `stowed-element-builder.ts`).
 *
 * The four directional offsets (AC 2.2/4.4) are enabled only while "Bounding box" is active, each
 * independently editable/committed (no linking, per AC 2.2). The shared preset/slider/input
 * control above them applies the same value to all four sides at once when used — a convenience
 * shortcut, not a linkage. For "Wrap Shape", all four directional boxes stay disabled — the SAME
 * shared control is instead the ONLY active offset editor for this style, persisting only
 * `wrapOffsetTop` (the uniform contour offset — see `commitAllOffsets`). The disabled boxes still
 * visually mirror that one value (AC 2.4/2.5 — see `offsetStringsFromObj`), so all four always
 * display the same number even though only Top is real data for this style. For "Jump", only
 * Top/Bottom are enabled and independently editable (AC 3.2/4.4) — Left/Right stay disabled since
 * Jump has no left/right text flow to offset; the shared control drives Top+Bottom together as a
 * convenience.
 *
 * Every offset defaults to 6pt (`DEFAULT_OFFSET`) for a target with no stored value yet, for every
 * wrap style including No Wrap (AC 2.5) — matching the 6pt preset so it's highlighted by default.
 *
 * ## Multi-selection (AC 7/8)
 *
 * `targets` generalizes the old single `target` into an array — one entry per eligible selected
 * shape/group/text frame. Every commit/live-preview path below loops `targets` and computes each
 * target's OWN before/after against ITS OWN current style (`normWrapStyle(t.obj)`), never a single
 * shared "the" style — two selected objects on different styles (e.g. a circle on "Wrap Shape" and
 * a triangle on "Bounding box") keep behaving as themselves, individually, right up until the user
 * explicitly picks one style or offset value to apply to the whole selection. With exactly one
 * target, every loop below runs exactly once over that one object — the single-selection path is
 * unchanged in outcome, just expressed as a length-1 case of the general one.
 *
 * - **Style buttons**: highlighted per `activeStyles` (the set of every selected target's own
 *   current style) — a mixed selection highlights every style actually present, never collapsing
 *   to "No Wrap" or a blank state (AC 8). Clicking a button always applies that style to every
 *   target when more than one is selected (no toggle-off); with exactly one target it still toggles
 *   off if already active, matching the pre-existing single-select behavior exactly.
 * - **Offset controls enablement**: a side/the shared control is enabled if ANY selected target's
 *   own style would enable it (`sideEnabledForStyle`/`offsetControlsEnabledForStyle`) — editing it
 *   still only ever writes fields that are real for each individual target's own style (never a
 *   disabled/inapplicable side on a target whose style doesn't use it).
 * - **Offset display**: `aggregateOffsetStrings`/`aggregateCommonOffset` reduce one
 *   `offsetStringsFromObj`/`commonOffsetFromObj` result per target into "same value" or "Mixed" —
 *   the exact same reasoning the single-object code already used across its four sides, just also
 *   applied across objects now.
 * - **Per-object offset memory**: `styleOffsetMemoryByIdRef` is a `Map` keyed by object id — each
 *   object's remembered per-style offsets live in their own entry for the component's lifetime, so
 *   nothing one object remembers can ever overwrite what another remembers, in single- or
 *   multi-selection alike.
 * - **Commit batching**: `commitToTargets` collects every target's before/after into one
 *   `BatchShapeTransformItem[]` and fires `onBatchShapesCommitted` ONCE — `home.tsx`'s existing
 *   batch-commit handler already accepts N-item batches (that's how multi-select move/resize
 *   already works), so a multi-object wrap/offset change becomes exactly one undo entry and one
 *   persistence batch, the same infrastructure already in place.
 * - **Live preview**: `applyLiveOffsetsToAll`/`applyLiveSideOffsetToAll` loop every target and
 *   patch its manager directly (canvas-visible immediately) — since shapes and text frames now
 *   live in one `ShapeManager`, patching a shape's wrap fields re-renders the whole tree on the
 *   same tick, so any nearby text frame picks up the new obstacle geometry without a separate
 *   cross-manager notification (unlike the old two-manager world).
 */
export function LayoutPanelTextWrap({
  shapeManagerRef,
  onBatchShapesCommitted,
  interactionLocked = false,
}: LayoutPanelTextWrapProps) {
  const { activeObjects, setActiveObjects } = useActiveObject()
  const sideInputs = useSideOffsetInputs()
  const commonInput = useCommonOffsetInput()
  const {
    targets,
    setStyleByTargetId,
    controlsDisabled,
    activeStyles,
    isOffsetDisabled,
    isSideOffsetDisabled,
  } = useWrapStyles(activeObjects, interactionLocked)

  useWrapOffsetSync({ targets, shapeManagerRef, setStyleByTargetId, sideInputs, commonInput })

  const commitToTargets = useCommitToTargets({
    shapeManagerRef,
    activeObjects,
    setActiveObjects,
    onBatchShapesCommitted,
  })
  const applyWrapStyle = useWrapStyleSwitch({ targets, setStyleByTargetId, commitToTargets })
  const { commitOffset, commitAllOffsets, tryCommitCommonInput } = useOffsetCommit({
    targets,
    sideInputs,
    commonInput,
    commitToTargets,
  })

  return (
    <TooltipProvider>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground leading-none">Text Wrap</label>
        <WrapStyleButtons
          activeStyles={activeStyles}
          disabled={controlsDisabled}
          singleTarget={targets.length === 1}
          onApplyStyle={applyWrapStyle}
        />

        <OffsetPresetToggle
          value={commonInput.commonSelectPreset}
          disabled={isOffsetDisabled}
          onSelect={commitAllOffsets}
        />

        <CommonOffsetSlider
          disabled={isOffsetDisabled}
          sliderValue={commonInput.commonSliderValue}
          inputText={commonInput.commonInputText}
          errorMessage={commonInput.commonInputErrorMessage}
          valuesMixed={commonInput.commonValuesMixed}
          onSliderDrag={(n) => {
            commonInput.previewSlider(n)

            // Live-preview every drag tick on the actual canvas, for every selected target
            // (AC — real-time wrap sync) — the real commit (undo entry + persistence) still
            // only happens once, in `onSliderCommit`, when the drag ends.
            applyLiveOffsetsToAll(shapeManagerRef, targets, n)
          }}
          onSliderCommit={commitAllOffsets}
          onInputChange={(raw) => {
            const { message, liveValue } = validateOffsetInput(raw)

            // Always show exactly what was typed — letters included — and validate after,
            // matching the Kerning/Leading (and Font Size) pattern: the field never blocks
            // a keystroke, it just flags an invalid one.
            commonInput.showTyped(raw, message)

            // Live-preview a valid, in-range keystroke on the canvas immediately, for
            // every selected target — the real commit (undo entry + persistence) still
            // only happens on blur/Enter, matching the slider's split.
            if (liveValue !== null) applyLiveOffsetsToAll(shapeManagerRef, targets, liveValue)
          }}
          onInputFocus={() => {
            commonInput.commonInputFocused.current = true
          }}
          onInputBlur={(raw) => {
            commonInput.commonInputFocused.current = false
            tryCommitCommonInput(raw)
          }}
        />

        {/* Directional offsets — all four enabled for Bounding Box (AC 2.2/4.4); only Top/Bottom
            enabled for Jump (AC 3.2/4.4); all four disabled for Wrap Shape (AC 4.4) — the shared
            control above is its only active editor. All four disabled for No Wrap too. Enabled
            per side if ANY selected target's own style uses it (AC 7/8); a "Mixed" placeholder
            shows when the applicable targets disagree on that side's value. */}
        <div className="grid grid-cols-2 gap-2 pt-1">
          {OFFSET_SIDES.map((side) => (
            <SideOffsetInput
              key={side.key}
              side={side}
              disabled={isSideOffsetDisabled(side.key)}
              value={sideInputs.offsetInputs[side.key]}
              mixed={sideInputs.offsetMixed[side.key]}
              errorMessage={sideInputs.offsetErrorMessages[side.key]}
              onChange={(raw) => {
                const { message, liveValue } = validateOffsetInput(raw)

                // Always show exactly what was typed — letters included — and validate
                // after, matching the Kerning/Leading (and Font Size) pattern: the field
                // never blocks a keystroke, it just flags an invalid one.
                sideInputs.showTyped(side.key, raw, message)

                // Live-preview a valid, in-range keystroke on the canvas immediately, for
                // every applicable selected target — the real commit (undo entry +
                // persistence) still only happens on blur/Enter, in `commitOffset`.
                // Only this one side is patched — independent per-side edits must never touch
                // the other sides (AC 4), nor a target whose style doesn't use this side
                // (AC 7).
                if (liveValue !== null) {
                  applyLiveSideOffsetToAll(shapeManagerRef, targets, side.key, liveValue)
                }
              }}
              onFocus={() => sideInputs.setFocused(side.key, true)}
              onBlur={(raw) => {
                sideInputs.setFocused(side.key, false)

                if (sideInputs.offsetErrorMessages[side.key] !== '' || raw === '') {
                  sideInputs.revert(side.key)
                  return
                }

                commitOffset(side.key, raw)
              }}
            />
          ))}
        </div>
      </div>
    </TooltipProvider>
  )
}
